/**
 * Tiền là số nguyên đồng. Không dùng số thực ở bất cứ đâu:
 * 0.1 + 0.2 !== 0.3, và một hoá đơn lệch 1 đồng là mất niềm tin của chủ quán.
 */
export type Vnd = number

const nf = new Intl.NumberFormat('vi-VN')

export const money = (v: number): string => nf.format(Math.round(v))

export const moneyD = (v: number): string => `${money(v)}đ`

/** Rút gọn cho ô thống kê: 1.845.000 → "1,8tr". Chỉ dùng chỗ không phải đối chiếu. */
export function moneyShort(v: number): string {
  const a = Math.abs(v)
  if (a >= 1_000_000_000) return `${(v / 1_000_000_000).toFixed(1).replace('.', ',')}tỷ`
  if (a >= 1_000_000) return `${(v / 1_000_000).toFixed(1).replace('.', ',')}tr`
  if (a >= 10_000) return `${Math.round(v / 1000)}k`
  return money(v)
}

/** Số lượng lưu dạng phần nghìn để bán theo cân: 1250 = 1,25 kg. */
export const QTY_UNIT = 1000

export const qtyToText = (milli: number): string =>
  milli % QTY_UNIT === 0 ? String(milli / QTY_UNIT) : (milli / QTY_UNIT).toFixed(2).replace('.', ',')

export const lineTotal = (unitPrice: Vnd, qtyMilli: number): Vnd =>
  Math.round((unitPrice * qtyMilli) / QTY_UNIT)

/** Mệnh giá khách hay đưa. Bốn nút này phủ gần hết trường hợp thu tiền mặt thật. */
export const CASH_CHIPS: Vnd[] = [20_000, 50_000, 100_000, 200_000, 500_000]

/**
 * Gợi ý tờ tiền khách có thể đưa, dựa trên tổng hoá đơn.
 * 78.000 → gợi ý 80.000 · 100.000 · 200.000, chứ không phải 4 mệnh giá cố định
 * mà cái nào cũng nhỏ hơn tổng tiền.
 */
export function cashSuggestions(total: Vnd): Vnd[] {
  if (total <= 0) return CASH_CHIPS.slice(0, 4)

  const out = new Set<Vnd>()
  out.add(total) // khách đưa đúng tiền

  const roundUp = (step: number) => Math.ceil(total / step) * step
  for (const step of [1_000, 10_000, 50_000, 100_000]) {
    const v = roundUp(step)
    if (v > total) out.add(v)
  }
  for (const chip of CASH_CHIPS) if (chip > total) out.add(chip)

  return [...out].sort((a, b) => a - b).slice(0, 5)
}

/** "38 phút" · "2 giờ 05" — hiện trên ô bàn để biết khách ngồi bao lâu. */
export function elapsedText(fromIso: string, now: number = Date.now()): string {
  const mins = Math.max(0, Math.floor((now - new Date(fromIso).getTime()) / 60_000))
  if (mins < 60) return `${mins} phút`
  const h = Math.floor(mins / 60)
  const m = mins % 60
  return `${h} giờ ${String(m).padStart(2, '0')}`
}

/** Số phút đã trôi qua — dùng để đổi màu cảnh báo khi bàn ngồi quá lâu. */
export const elapsedMinutes = (fromIso: string, now: number = Date.now()): number =>
  Math.max(0, Math.floor((now - new Date(fromIso).getTime()) / 60_000))

const timeFmt = new Intl.DateTimeFormat('vi-VN', { hour: '2-digit', minute: '2-digit' })
const dayFmt = new Intl.DateTimeFormat('vi-VN', { day: '2-digit', month: '2-digit' })

export const clock = (iso: string): string => timeFmt.format(new Date(iso))
export const dayMonth = (iso: string): string => dayFmt.format(new Date(iso))

/** "hôm nay 19:30" · "12/09 19:30" — dùng cho giờ đặt bàn. */
export function whenText(iso: string, now: Date = new Date()): string {
  const d = new Date(iso)
  const sameDay = d.toDateString() === now.toDateString()
  const tomorrow = new Date(now)
  tomorrow.setDate(now.getDate() + 1)
  if (sameDay) return `hôm nay ${timeFmt.format(d)}`
  if (d.toDateString() === tomorrow.toDateString()) return `mai ${timeFmt.format(d)}`
  return `${dayFmt.format(d)} ${timeFmt.format(d)}`
}
